/* eslint-disable max-lines */
import React, { useState, useCallback, useEffect, useContext } from 'react';
import _ from 'lodash';
import axios from 'axios';
import { styled } from '@this/constants/themes';
import type { RouteComponentProps } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import type { Moment } from 'moment';
import moment from 'moment';
import Typography from '@material-ui/core/Typography';
import InputLabel from '@material-ui/core/InputLabel';
import Card from '@material-ui/core/Card';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import type { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import PlaceIcon from '@material-ui/icons/Place';
import CancelIcon from '@material-ui/icons/Cancel';
import type { AdjustmentResponseArgs } from '@this/domain/appoint/adjustment';
import Adjustment, { convertAdjustmentResponseToArgs } from '@this/domain/appoint/adjustment';
import type { CalendarArgs } from '@this/domain/appoint/calendar';
import Calendar from '@this/domain/appoint/calendar';
import Candidate from '@this/domain/appoint/candidate';
import type { EventResponse } from '@this/domain/appoint/event';
import Event, { convertEventResponseToArgs } from '@this/domain/appoint/event';
import AppointContext from '@this/components/appoint/appoint_context';
import muiTheme from '@this/components/appoint/mui_theme';
import Utils from '@this/domain/appoint/utils';
import BigCalendar from './show_big_calendar';
import {
  H2,
  Wrapper,
  Left,
  Right,
  LoadingBackdrop,
  FilledTextField,
  ButtonWrapper,
  ButtonProgress
} from '../../common';
import { AuthBackdrop, TheReAuthButton } from '../new/new';
import AdjustmentsShowCandidateArea from './candidate_area';
import FixAdjustmentDialog from '../../shared/fix_adjustment_dialog/fix_adjustment_dialog';
import type { DestinationInfo } from '../../shared/fixed_adjustment_dialog/fixed_adjustment_dialog';
import FixedAdjustmentDialog from '../../shared/fixed_adjustment_dialog/fixed_adjustment_dialog';

interface Props extends RouteComponentProps<{ key?: string }> {
  handleAdjustmentChange?: () => void;
  setFixWithCalendar: (v: boolean | null) => void;
}

interface Response {
  primary_calendar_id: string;
  calendars: CalendarArgs[];
  disconnected_accounts: { email: string; message: string | null }[];
  is_preview: boolean;
  adjustment: AdjustmentResponseArgs;
  calendar_connected: boolean;
  link_aitravel: boolean;
  add_to_calendar_url?: string;
  download_ics_url?: string;
  return_url: string;
  error?: string;
}

interface AuthoritiesResponse {
  is_owner: boolean;
}

interface EventsResponse {
  events: EventResponse[][];
}

interface SubmitParams {
  start: string;
  end: string;
  participants: { id: string; provider: string | null; auth_data_id: string | undefined }[];
  share_destinations: DestinationInfo[];
}

const AdjustmentsShow = observer((props: Props) => {
  const contextValue = useContext(AppointContext);
  const history = useHistory();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [adjustment, setAdjustment] = useState<Adjustment | null>(null);
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [disconnectedAccounts, setDisconnectedAccounts] = useState<{ email: string; message: string | null }[]>(
    []
  );
  const [eventLists, setEventLists] = useState<Event[][]>([[]]);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null | undefined>(undefined);
  const [isOwner, setIsOwner] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [needReAuth, setNeedReAuth] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [date, setDate] = useState<Moment>(moment());
  const [selectedDate, setSelectedDate] = useState<MaterialUiPickersDate>(null);
  const [isEventLoading, setIsEventLoading] = useState<boolean>(false);
  const [calendarConnected, setCalendarConnected] = useState<boolean>(false);
  const [selectedParticipants, setSelectedParticipants] = useState<Calendar[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [showFixedDialog, setShowFixedDialog] = useState<boolean>(false);
  const [showPromotionDialog, setShowPromotionDialog] = useState<boolean>(false);

  // カレンダーが月曜始まりなので、startMomentには「今週の月曜日」を表示したい。
  // 2021/06/06(日)時点では、2021/05/31(月)をstartMomentにセットしたい。
  // 2021/06/07(月)時点では、2021/06/07(月)をstartMomentにセットしたい。
  // day(1)は、曜日番号を1に変更するだけなので、日曜日(0)から見て前週の月曜日を取得するためには、
  // 1日戻る必要があるためsubtract(1, 'days')を設定している。
  const startMoment = date.clone().subtract(1, 'days').day(1);
  const endMoment = startMoment.clone().add(6, 'days');
  const startDate = startMoment.format('YYYY-MM-DD');
  const endDate = endMoment.format('YYYY-MM-DD');

  const getParams = new URLSearchParams(window.location.search);
  const paramRefApp = getParams.get('ref_app');
  const paramOrgnzID = getParams.get('organization_id');
  const paramAppID = getParams.get('application_id');
  let defaultSlotStart: string = getParams.get('selectedSlot') || '';

  const key = props.match.params.key || props.location.hash.replace(/^#/, '');

  const setCreatedMessageOnSnackbar = (title: string) => {
    location.hash = '';
    contextValue.setSnackbarContent({
      message: `候補日時を登録しました。\n「【調整中】${title}」という名前で予定を参加者のカレンダーに登録しました。`,
      severity: 'success'
    });
    contextValue.setOpenSnackbar(true);
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    props.setFixWithCalendar(false);
    const res = await axios.get<AuthoritiesResponse>(`/aicalendar/adjustments/${key}/authorities.json`);
    const authority = res.data;
    setIsOwner(authority.is_owner);
    const locationSearch = location.search;
    const adjRes = await axios.get<Response>(`/aicalendar/adjustments/${key}.json${locationSearch}`);
    const data = adjRes.data;
    const args = convertAdjustmentResponseToArgs(data.adjustment);
    setNeedReAuth(data.disconnected_accounts.length > 0);
    const calendars = data.calendars.map(c => new Calendar(c));
    setCalendars(calendars);
    setDisconnectedAccounts(data.disconnected_accounts);
    if (authority.is_owner) {
      Utils.trackEvent('[cal][view] adjustments show (as organizer)');
    } else {
      Utils.trackEvent('[cal][view] adjustments show (as guest)');
    }
    const fetchedAdjustment = new Adjustment(args);
    setAdjustment(fetchedAdjustment);
    let defaultDate = fetchedAdjustment.candidates[0].start;
    const dateParam = locationSearch?.match(/selectedSlot=\d{4}-\d{2}-\d{2}/);
    if (dateParam) {
      const dateString = dateParam[0].replace('selectedSlot=', '');
      if (fetchedAdjustment.candidates.find(c => c.start.format('YYYY-MM-DD') === dateString)) {
        defaultDate = moment(dateString);
      }
    }
    setDate(defaultDate);
    setSelectedDate(defaultDate);
    setCalendarConnected(data.calendar_connected);
    const primary = calendars.find(c => c.id === data.primary_calendar_id) || null;
    if (primary) {
      setSelectedParticipants([primary]);
    }
    setShowPromotionDialog(!authority.is_owner && !calendarConnected && !fetchedAdjustment.adjusting);
    setIsLoading(false);
    if (location.hash === '#new') {
      setCreatedMessageOnSnackbar(fetchedAdjustment.title);
    }
  }, [adjustment]);

  // マウント時に1回発動
  useEffect(() => {
    fetchData();
  }, []);

  const fetchEvents = useCallback(async () => {
    if (!adjustment) {
      return;
    }
    setIsEventLoading(true);
    const participants = isOwner ? adjustment.participants : selectedParticipants;
    const params = {
      calendars: participants.map(p => (p ? { id: p.id, provider: p.provider } : null)),
      from: startDate,
      to: endDate
    };
    if (params.calendars.length > 0) {
      const res = await axios.get<EventsResponse>('/aicalendar/events.json', { params });
      const data = res.data;
      setEventLists(data.events.map(evts => evts.map(e => new Event(convertEventResponseToArgs(e)))));
    }
    setIsEventLoading(false);
  }, [adjustment ? adjustment.participants : null, contextValue.user, selectedParticipants, startDate, endDate]);

  const toggleSelectedCandidate = (candidate: Candidate) => {
    setSelectedCandidate(
      selectedCandidate && selectedCandidate.start.format() === candidate.start.format() ? null : candidate
    );
  };

  useEffect(() => {
    fetchEvents();
  }, [contextValue.user, selectedParticipants, startDate, endDate]);

  const handleConfirmDialogToggle = (v: boolean) => {
    setShowConfirmDialog(v);
    props.setFixWithCalendar(v ? null : false);
  };

  const handleSubmit = async (destinationName: string, destinationMailAddress: string) => {
    if (!selectedCandidate) {
      return;
    }
    const participants = adjustment?.participants.map(p => p.submitParams()) || [];
    if (participants) {
      if (contextValue.user) {
        participants.push({ id: contextValue.user.email, provider: '', auth_data_id: '' });
      }
      participants.push({ id: destinationMailAddress, provider: '', auth_data_id: '' });
    }
    if (participants) participants.push({ id: destinationMailAddress, provider: '', auth_data_id: '' });
    Utils.trackEvent('[cal][click] fix adjustment');
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const params: SubmitParams = {
        start: selectedCandidate.start.format(),
        end: selectedCandidate.end.format(),
        participants,
        share_destinations: [{ name: destinationName, email: destinationMailAddress }]
      };
      if (!isOwner && calendarConnected) {
        params.participants.concat(selectedParticipants.map(p => p.submitParams()));
      }
      const res = await axios.put<Response>(`/aicalendar/adjustments/${key}.json`, params);
      if (res?.data && res.data.error) {
        setErrorMessage(res.data.error);
      } else {
        if (paramRefApp && paramOrgnzID && paramAppID && adjustment && res.data.return_url) {
          if (adjustment.refApp && adjustment.organizationId && adjustment.applicationId) {
            if (
              paramRefApp === adjustment.refApp &&
              paramOrgnzID === adjustment.organizationId &&
              paramAppID === adjustment.applicationId
            ) {
              location.href = history.createHref({ pathname: '/external_connections' });
            }
          }
        }
        fetchData();
      }
    } catch (e) {
      setErrorMessage('通信エラーが発生しました。時間をおいて再度お試しください。');
    } finally {
      if (props.handleAdjustmentChange !== undefined) {
        props.handleAdjustmentChange();
      }
      setIsSubmitting(false);
      setShowConfirmDialog(false);
      setShowFixedDialog(true);
      props.setFixWithCalendar(null);
    }
  };

  const handleScheduleShare = async (share_destinations: DestinationInfo[]) => {
    Utils.trackEvent('[cal][click] fix adjustment');
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      // todo: share_destinationsに複数格納可とする
      const params: SubmitParams = {
        start: '',
        end: '',
        participants: [],
        share_destinations
      };
      if (!isOwner && calendarConnected) {
        params.participants.concat(selectedParticipants.map(p => p.submitParams()));
      }
      await axios.put<Response>(`/aicalendar/adjustments/${key}.json`, params);
    } catch (e) {
      setErrorMessage('通信エラーが発生しました。時間をおいて再度お試しください。');
    } finally {
      if (props.handleAdjustmentChange !== undefined) {
        props.handleAdjustmentChange();
      }
      setIsSubmitting(false);
    }
    setShowFixedDialog(false);
  };

  //
  // 以下、stateから計算しているだけのもの。どっかでstore化した方がよさそう
  //
  const slotsForCandidate = (candidate: Candidate) => {
    const slots: Candidate[] = [];
    if (adjustment) {
      const stepMinutes = 30;
      let slotStart = candidate.start.clone();
      while (true) {
        const slotEnd = slotStart.clone().add(adjustment.duration, 'minutes');
        if (slotEnd > candidate.end) {
          break;
        }
        slots.push(new Candidate({ start: slotStart, end: slotEnd }));
        slotStart = slotStart.clone().add(stepMinutes, 'minutes');
      }
    }
    return slots;
  };

  let candidateSlots: Candidate[] = [];
  if (adjustment) {
    let selectedCandidates = adjustment.candidates.filter(c => {
      return selectedDate && c.start.format('YYYY-MM-DD') === selectedDate.format('YYYY-MM-DD');
    });
    if (selectedCandidates.length === 0 || contextValue.isMobile) {
      selectedCandidates = adjustment.candidates;
    }
    candidateSlots = selectedCandidates
      .map(candidate => {
        return slotsForCandidate(candidate);
      })
      .flat();
    if (defaultSlotStart.length > 0 && selectedCandidate === undefined) {
      const splitSelectedCandidate = defaultSlotStart.split('-');
      let formatCandidateStart = 'YYYY-MM-DD_HH:mm';
      if (splitSelectedCandidate.length === 2) {
        defaultSlotStart = splitSelectedCandidate[0];
        formatCandidateStart = 'YYYYMMDDHHmm';
      }
      setSelectedCandidate(
        candidateSlots.find(s => s.start.format(formatCandidateStart) === defaultSlotStart) || null
      );
    }
  }

  const handleSelectedDateChange = (date: MaterialUiPickersDate) => {
    if (date === null) {
      return;
    }

    setSelectedDate(date);
    setDate(date);
  };

  const renderPromotionDialog = () => (
    <PromotionWrap style={{ display: `${showPromotionDialog ? 'flex' : 'none'}` }}>
      <Paper style={{ position: 'relative', margin: 'auto', maxWidth: '230px', padding: '30px' }}>
        <IconButton
          size="small"
          onClick={() => setShowPromotionDialog(false)}
          style={{ position: 'absolute', top: 4, right: 4, zIndex: 10 }}
        >
          <CloseButton />
        </IconButton>
        <Box>
          <img src="/images/aicalendar/adjustment_promotion1.png" width="230" height="154" />
        </Box>
        <Box>
          <PromotionTit style={{ margin: '15px 0 10px' }}>
            自分の予定と候補日を一緒にカレンダーに表示しませんか？
          </PromotionTit>
          <PromotionTxt>
            AIカレンダーに登録すると候補日と同時にあなたの予定をカレンダーに表示できます。
          </PromotionTxt>
          <PromotionTxtSub>第三者に閲覧されることはありません。</PromotionTxtSub>
        </Box>
        <Box style={{ marginTop: '12px', textAlign: 'center' }}>
          <Button
            variant="outlined"
            color="primary"
            href={history.createHref({ pathname: '/users/sign_up' })}
            target="_blank"
            style={{ fontSize: '11px' }}
          >
            AIカレンダーにログイン/登録
            <span style={{ fontSize: '9px' }}>（無料）</span>
          </Button>
        </Box>
      </Paper>
    </PromotionWrap>
  );

  const renderCandidatesArea = () => (
    <AdjustmentsShowCandidateArea
      adjustment={adjustment}
      candidateSlots={candidateSlots}
      selectedCandidate={selectedCandidate}
      setSelectedCandidate={setSelectedCandidate}
      selectedDate={selectedDate}
      setSelectedDate={handleSelectedDateChange}
      toggleSelectedCandidate={toggleSelectedCandidate}
    />
  );

  const renderBigCalendar = () => (
    <BigCalendarWrapper active={`${adjustment?.adjusting}`}>
      <BigCalendar
        eventLists={eventLists}
        candidates={adjustment ? adjustment.candidates : []}
        candidateSlots={adjustment ? _.flatten(_.map(adjustment.candidates, c => slotsForCandidate(c))) : []}
        selectedCandidate={selectedCandidate}
        calendars={calendars}
        date={date}
        onDateChange={date => setDate(date)}
        isEventLoading={isEventLoading}
        setSelectedDate={handleSelectedDateChange}
        setSelectedCandidate={setSelectedCandidate}
      />
    </BigCalendarWrapper>
  );

  const ReAuthBackdrop = () => (
    <AuthBackdrop open={needReAuth}>
      <Box>
        <Typography style={{ textAlign: 'center' }}>
          {disconnectedAccounts.map((da, i) => (
            <p key={i}>
              {da.message
                ? `Google Calendar(${da.email})への${da.message}`
                : `Google Calendar(${da?.email})との連携が解除されています`}
            </p>
          ))}
        </Typography>
        <TheReAuthButton
          variant="contained"
          color="primary"
          style={{
            display: 'block',
            margin: '12px auto 0',
            textTransform: 'none'
          }}
          onClick={() => {
            location.href = history.createHref({ pathname: '/auth/new' });
          }}
        >
          Google Calendarと再連携
        </TheReAuthButton>
      </Box>
    </AuthBackdrop>
  );

  const renderFixedDialog = () => (
    <>
      {adjustment && showFixedDialog && (
        <FixedAdjustmentDialog
          show={showFixedDialog}
          onClose={() => setShowFixedDialog(false)}
          title={adjustment.title}
          startTime={adjustment.startTime}
          endTime={adjustment.endTime}
          meetingType={adjustment.meetingType || ''}
          onlineMeetingName={adjustment.onlineMeetingName() || ''}
          onlineMeetingUrl={adjustment.onlineMeetingUrl() || ''}
          placeName={adjustment.place || adjustment.placeAddress}
          description={adjustment.description || ''}
          onSubmit={handleScheduleShare}
          isSubmitting={isSubmitting}
          errorMessage={errorMessage}
        />
      )}
    </>
  );

  return (
    <Wrapper
      style={{
        overflow: 'hidden',
        maxWidth: contextValue.isMobile ? '100vw' : 'auto',
        minHeight: 'calc(100vh - 54px)'
      }}
    >
      <ReAuthBackdrop />
      <LoadingBackdrop open={isLoading} />
      <TheLeft isMobile={contextValue.isMobile} withCalendar={contextValue.fixWithCalendar || false}>
        <Box
          style={{
            marginTop: '16px',
            maxHeight: `calc(100vh - 54px - 24px - 24px${contextValue.isMobile ? ' - 56px' : ''})`,
            overflowY: 'scroll',
            color: adjustment?.adjusting ? 'inherit' : '#909090'
          }}
        >
          <LeftInfoBox>
            {adjustment && (
              <>
                {!adjustment.adjusting && (
                  <LeftStatus>
                    この予定は
                    {adjustment.fixed
                      ? '確定済みです'
                      : adjustment.cancelled
                      ? 'キャンセルされました'
                      : '期限切れです'}
                  </LeftStatus>
                )}
                <Box>
                  <H2
                    style={{
                      margin: '0 auto 6px 0',
                      fontSize: '16px',
                      color: adjustment.adjusting ? 'inherit' : '#909090'
                    }}
                  >
                    {adjustment.title}
                  </H2>
                </Box>
                <LeftInfoTable>
                  <LeftInfoTr>
                    <LeftInfoTh>
                      <TheInfoIcon>
                        <CalendarTodayIcon fontSize="small" />
                      </TheInfoIcon>
                      <span>候補日</span>
                    </LeftInfoTh>
                    <LeftInfoTd>
                      {adjustment.startTime && adjustment.endTime ? (
                        <>
                          {adjustment.startTime.format('MM月DD日 HH:mm 〜 ')}
                          {adjustment.endTime.format('HH:mm')}
                        </>
                      ) : (
                        <>
                          {adjustment.startDate()?.format('MM月DD日 〜 ')}
                          {adjustment.lastStartDate()?.format('MM月DD日')}
                        </>
                      )}
                    </LeftInfoTd>
                  </LeftInfoTr>
                  <LeftInfoTr>
                    <LeftInfoTh>
                      <TheInfoIcon>
                        <AccessTimeIcon fontSize="small" />
                      </TheInfoIcon>
                      <span>時間</span>
                    </LeftInfoTh>
                    <LeftInfoTd>{`${adjustment.duration || '- '}分`}</LeftInfoTd>
                  </LeftInfoTr>
                  <LeftInfoTr>
                    <LeftInfoTh>
                      <TheInfoIcon>
                        <PlaceIcon fontSize="small" />
                      </TheInfoIcon>
                      <span>場所</span>
                    </LeftInfoTh>
                    <LeftInfoTd>
                      {adjustment.meetingType && adjustment.meetingType !== 'none'
                        ? 'オンライン'
                        : adjustment?.place || adjustment?.placeAddress}
                    </LeftInfoTd>
                  </LeftInfoTr>
                </LeftInfoTable>
              </>
            )}
          </LeftInfoBox>
          {adjustment && adjustment.adjusting && (
            <LeftCandidateBox>
              <LeftCandidateTitle>候補を選択</LeftCandidateTitle>
              {renderCandidatesArea()}
              <LeftButtonWrapper isMobile={contextValue.isMobile}>
                {contextValue.isMobile && contextValue.fixWithCalendar && (
                  <LeftButtonSideText>
                    {selectedCandidate ? (
                      <>{selectedCandidate.start.format('MM/DD (ddd) HH:mm -')}</>
                    ) : (
                      <>候補日：未選択</>
                    )}
                  </LeftButtonSideText>
                )}
                <Button
                  variant="contained"
                  fullWidth
                  color="primary"
                  disabled={!selectedCandidate || isSubmitting}
                  onClick={() => handleConfirmDialogToggle(true)}
                >
                  この日時で確定する
                </Button>
                {isSubmitting && <ButtonProgress />}
              </LeftButtonWrapper>
              {errorMessage && <Error color="error">{errorMessage}</Error>}
            </LeftCandidateBox>
          )}
          {!isLoading && contextValue.isMobile && !contextValue.user && (
            <LeftSignupBox>
              <LeftSignupRibon>
                <img src="/images/aicalendar/logo_ribon01.png" width="82" height="80" />
              </LeftSignupRibon>
              <LeftSignupBoxIn>
                <LeftSignupTitle>AIカレンダーに登録またはログイン</LeftSignupTitle>
                <Button
                  style={{ display: 'block', margin: '5px 0 0 -10px' }}
                  onClick={() => {
                    location.href = contextValue.googleOauthPath;
                  }}
                >
                  <img src="/images/sign_in_with_google.png" width="191" />
                </Button>
                <Button
                  style={{ display: 'block', margin: '-5px 0 0 -7px' }}
                  onClick={() => {
                    location.href = contextValue.outlookOauthPath;
                  }}
                >
                  <img src="/images/sign_in_with_microsoft.png" width="215" />
                </Button>
              </LeftSignupBoxIn>
            </LeftSignupBox>
          )}
          {selectedCandidate && (
            <FixAdjustmentDialog
              show={showConfirmDialog}
              onClose={() => handleConfirmDialogToggle(false)}
              startTime={selectedCandidate.start}
              endTime={selectedCandidate.end}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              errorMessage={errorMessage}
            />
          )}
          {renderFixedDialog()}
        </Box>
      </TheLeft>
      <TheRight isMobile={contextValue.isMobile} withCalendar={contextValue.fixWithCalendar || false}>
        {renderBigCalendar()}
        {renderPromotionDialog()}
      </TheRight>
    </Wrapper>
  );
});

const PromotionWrap = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const CloseButton = styled(CancelIcon)`
  color: #ddd;
  font-size: 20px;
`;

const PromotionTit = styled(Typography)`
  font-size: 15px;
  font-weight: bold;
  line-height: 1.6;
`;

const PromotionTxt = styled(Typography)`
  font-size: 11px;
  line-height: 1.8;
`;

const PromotionTxtSub = styled(PromotionTxt)`
  color: #606060;
`;

export const TheCard = styled(Card)`
  padding: 24px;
  margin-bottom: 32px;
`;

export const CardTitle = styled(Typography).attrs({ variant: 'h3' })`
  margin-bottom: 24px;
  padding-top: 9px;
  font-size: 24px;
  line-height: 32px;
  font-weight: 500;
`;

export const TheDescription = styled(Typography)`
  font-size: 16px;
  line-height: 28px;
`;

export const CandidatesButtonBox = styled.div`
  box-sizing: border-box;
  width: 50%;
  padding: 2px;
`;

export const Error = styled(Typography).attrs(() => ({ color: 'error' }))`
  margin-top: 12px;
`;

export const IconArea = styled(Box)`
  min-width: 33px;
  padding-top: 5px;
`;

export const TheInputLabel = styled(InputLabel)`
  margin-bottom: 8px;
  font-size: 16px;
  line-height: 28px;
`;

export const NameField = styled(FilledTextField)`
  width: 280px;
`;

const BigCalendarWrapper = styled(Box)<{ active: string }>`
  ${props =>
    props.active === 'false' &&
    `
    opacity: 0.6;
  `}
`;

const TheLeft = styled(Left)<{ isMobile: boolean; withCalendar: boolean }>`
  width: 280px;
  margin: 0;
  padding-bottom: 24px;
  border-top: solid 10px ${muiTheme.palette.primary.main};

  ${props =>
    props.isMobile &&
    `
    max-height: calc(100vh - 54px);

    ${
      props.withCalendar
        ? `
      width: 0;
      overflow: hidden;
    `
        : `
      width: 100vw;
    `
    }
  `}
`;

const LeftInfoBox = styled(Box)`
  margin-bottom: 20px;
  padding: 0 20px;
`;

const LeftStatus = styled(Typography).attrs(() => ({ color: 'error' }))`
  margin-bottom: 20px;
  font-size: 12px;
  font-weight: bold;
`;

const LeftInfoTable = styled.table``;

const LeftInfoTr = styled.tr``;

const LeftInfoTh = styled.th`
  padding: 6px 0;
  color: #909090;
  font-size: 10px;
  text-align: left;
`;

const LeftInfoTd = styled.td`
  padding: 6px;
  vertical-align: middle;
  font-size: 12px;
`;

const TheInfoIcon = styled.span`
  display: inline-block;
  vertical-align: middle;
  height: 20px;
  margin-right: 4px;
`;

const LeftCandidateBox = styled(Box)``;

const LeftCandidateTitle = styled(Typography)`
  padding: 10px 17px;
  background: #f9f9f9;
  border-left: solid 3px ${muiTheme.palette.primary.main};
  font-size: 12px;
  line-height: 1;
`;

const LeftButtonWrapper = styled(ButtonWrapper)<{ isMobile: boolean }>`
  box-sizing: border-box;
  width: 100%;
  padding: 0 24px;

  ${props =>
    props.isMobile &&
    `
    display: flex;
    align-items: center;
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 90;
    padding: 10px 24px;
    background: #f9f9f9;
    box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2);
  `}
`;

const LeftButtonSideText = styled(Typography)`
  width: 100%;
  font-size: 14px;
  font-weight: 700;
`;

const LeftSignupBox = styled(Box)`
  position: relative;
  padding: 5px 22px 20px;
`;

const LeftSignupRibon = styled(Box)`
  position: absolute;
  top: 0;
  right: 17px;
  width: 82px;
  height: 80px;
`;

const LeftSignupBoxIn = styled(Box)`
  padding: 20px 22px;
  background: #f9f9f9;
  border-radius: 10px;
`;

const LeftSignupTitle = styled(Typography)`
  color: ${muiTheme.palette.primary.main};
  font-size: 14px;
  font-weight: bold;
`;

const TheRight = styled(Right)<{ isMobile: boolean; withCalendar: boolean }>`
  position: relative;
  height: 100%;
  padding: 12px 20px 0 24px;
  border-left: solid 1px ${props => props.theme.grayBorderColor};

  .rbc-toolbar button {
    padding: 0.275rem 1rem;
  }

  ${props =>
    props.isMobile &&
    `
    max-height: calc(100vh - 54px);
    padding: 0 6px;
    border-left: 0;

    ${
      props.withCalendar
        ? `
      width: 100vw;
    `
        : `
      width: 0;
      overflow: hidden;
    `
    }
  `}
`;

export const AutocompleteBox = styled.div<{ withAlert?: string }>`
  width: 100%;
  .MuiAutocomplete-inputRoot.MuiFilledInput-root {
    padding-top: 6px;
  }

  ${props =>
    props.withAlert === 'true' &&
    `
    input::placeholder {
      color: ${muiTheme.palette.error.main};
      opacity: 1;
    }
    .MuiInput-underline:before {
      border-bottom-color: ${muiTheme.palette.error.main};
    }
  `}
`;

export default AdjustmentsShow;
